import type { RowSelectionState } from "@tanstack/react-table";
import { PackagePlus, Plus, RotateCw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input } from "~/components/ui";
import type { FilterUserOption, TenantRow } from "~/features/admin/tenants/types";
import { BulkInstallProductsDialog } from "./BulkInstallProductsDialog";
import { TenantFormDialog } from "./TenantFormDialog";
import { TenantPeek } from "./TenantPeek";
import { TenantsTable } from "./TenantsTable";
import { TenantUserFilter } from "./TenantUserFilter";

interface TenantsPresenterProps {
  rows: TenantRow[];

  search: string;
  onSearchChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;

  selectedUsers: FilterUserOption[];
  onUserFilterChange: (users: FilterUserOption[]) => void;

  selectedTenant: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;

  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
  onBulkInstallCompleted: () => void;
}

export function TenantsPresenter({
  rows,
  search,
  onSearchChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  selectedUsers,
  onUserFilterChange,
  selectedTenant,
  onSelect,
  onCloseSelected,
  rowSelection,
  onRowSelectionChange,
  onBulkInstallCompleted,
}: TenantsPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkInstallOpen, setBulkInstallOpen] = useState(false);

  const selectedRows = useMemo(() => rows.filter((row) => rowSelection[row.id]), [rows, rowSelection]);

  return (
    <div className="mx-auto max-w-(--content-max) space-y-5 p-8">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-dewey-title capitalize">{t("model.tenant.modelNames")}</h1>
            <div className="mt-1 text-14-regular text-fg-3 tabular-nums">
              {t("feature.tenants.subtitle", { count: rows.length })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t("ui.search")}
                className="h-9 w-[240px] pl-8"
                aria-label={t("ui.search")}
              />
            </div>
            <TenantUserFilter selectedUsers={selectedUsers} onChange={onUserFilterChange} />
            <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
              <RotateCw />
            </Button>
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus />
              <span>{t("ui.add")}</span>
            </Button>
          </div>
        </div>

        {selectedRows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2.5">
            <div className="text-14-regular tabular-nums text-fg-1">
              {t("feature.tenants.bulkInstall.selectedCount", { count: selectedRows.length })}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => onRowSelectionChange({})}>
                <X />
                <span>{t("feature.tenants.bulkInstall.clearSelection")}</span>
              </Button>
              <Button type="button" size="sm" onClick={() => setBulkInstallOpen(true)}>
                <PackagePlus />
                <span>{t("feature.tenants.bulkInstall.title")}</span>
              </Button>
            </div>
          </div>
        )}

        <TenantsTable
          rows={rows}
          onSelect={onSelect}
          emptyMessage={t("feature.tenants.empty")}
          globalFilter={search}
          onGlobalFilterChange={onSearchChange}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
        />
      </div>

      <TenantFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <TenantPeek id={selectedTenant} onClose={onCloseSelected} />
      <BulkInstallProductsDialog
        open={bulkInstallOpen}
        onClose={() => setBulkInstallOpen(false)}
        tenants={selectedRows}
        onCompleted={onBulkInstallCompleted}
      />
    </div>
  );
}
